import type { IUserData } from "../shared/session/interfaces/session";
import { PayoutMethodType, type PayoutInput } from "../interfaces/payout";
import type { ComposeSelection } from "../store/useComposeStore";

function resolvePayoutMethod(userData?: IUserData | null): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.payoutMethod) return pm.payoutMethod;
  switch (pm.type) {
    case "bank_account":
      return "BANK_ACCOUNT";
    case "cash":
      return "CASH_PICKUP";
    case "card":
      return "PUSH_TO_CARD";
    case "mobile_wallet":
      return "MOBILE_WALLET";
    default:
      return undefined;
  }
}

const toMethodType = (
  canonical: string | undefined,
): PayoutMethodType | null => {
  switch (canonical) {
    case "BANK_ACCOUNT":
      return PayoutMethodType.BANK_ACCOUNT;
    case "CASH_PICKUP":
      return PayoutMethodType.CASH_PICKUP;
    case "PUSH_TO_CARD":
      return PayoutMethodType.PUSH_TO_CARD;
    case "MOBILE_WALLET":
      return PayoutMethodType.MOBILE_WALLET;
    default:
      return null;
  }
};

/** Maps claim `userData.paymentMethod` into compose selection (default row). */
export function claimDefaultPayoutSelection(
  userData: IUserData | undefined | null,
): ComposeSelection | null {
  const pm = userData?.paymentMethod;
  if (!pm) return null;
  const canonical = resolvePayoutMethod(userData) ?? pm.payoutMethod;
  const method = toMethodType(canonical);
  if (!method) return null;

  const payout: PayoutInput = { source: "new" };

  if (method === PayoutMethodType.CASH_PICKUP) {
    const payer = pm.cash?.payer ?? pm.cash?.provider;
    if (payer) payout.payerId = payer.toUpperCase();
    const cashState = pm.cash?.state;
    if (cashState) payout.stateCode = cashState;
  } else if (method === PayoutMethodType.BANK_ACCOUNT) {
    const bank = pm.bankAccount;
    if (bank?.bankCode) payout.bankId = bank.bankCode.toUpperCase();
    if (bank?.accountNumber) payout.accountNumber = bank.accountNumber;
  } else if (method === PayoutMethodType.PUSH_TO_CARD) {
    const card = pm.card;
    if (card?.last4) payout.last4 = card.last4;
    if (card?.brand) payout.brand = card.brand;
  } else if (method === PayoutMethodType.MOBILE_WALLET) {
    const w = pm.mobileWallet;
    if (w?.carrier) payout.carrierId = w.carrier.toUpperCase();
    if (w?.phone) payout.phone = w.phone;
  }

  return { method, option: null, payout };
}
